import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';

const ONBOARDED_KEY = 'saved_profiles_onboarded';

type SavedContext = {
  onAuthRequired?: () => void;
  onFirstSave?: () => void;
};

// Simple in-memory pub/sub so all hook instances stay in sync
const listeners = new Set<() => void>();
let savedIdsCache: Set<string> | null = null;
let cachedUserId: string | null = null;

const notify = () => listeners.forEach((l) => l());

export const useSavedProfiles = (ctx?: SavedContext) => {
  const { user } = useAuth();
  const [savedIds, setSavedIds] = useState<Set<string>>(
    () => (cachedUserId === (user?.id ?? null) && savedIdsCache ? new Set(savedIdsCache) : new Set())
  );
  const [loading, setLoading] = useState(false);
  const ctxRef = useRef(ctx);
  ctxRef.current = ctx;

  const refresh = useCallback(async () => {
    if (!user) {
      savedIdsCache = new Set();
      cachedUserId = null;
      setSavedIds(new Set());
      return;
    }
    setLoading(true);
    const { data, error } = await supabase
      .from('saved_profiles')
      .select('saved_user_id')
      .eq('user_id', user.id);
    setLoading(false);
    if (error) {
      console.error('Error loading saved profiles:', error);
      return;
    }
    const next = new Set((data || []).map((r: any) => r.saved_user_id as string));
    savedIdsCache = next;
    cachedUserId = user.id;
    setSavedIds(new Set(next));
    notify();
  }, [user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    const l = () => {
      if (savedIdsCache) setSavedIds(new Set(savedIdsCache));
    };
    listeners.add(l);
    return () => {
      listeners.delete(l);
    };
  }, []);

  const isSaved = useCallback((id: string) => savedIds.has(id), [savedIds]);

  const toggleSave = useCallback(
    async (targetId: string, name?: string) => {
      if (!user) {
        ctxRef.current?.onAuthRequired?.();
        return;
      }
      if (targetId === user.id) return;

      const currentlySaved = savedIdsCache?.has(targetId) ?? false;

      if (currentlySaved) {
        // optimistic
        savedIdsCache?.delete(targetId);
        notify();
        const { error } = await supabase
          .from('saved_profiles')
          .delete()
          .eq('user_id', user.id)
          .eq('saved_user_id', targetId);
        if (error) {
          savedIdsCache?.add(targetId);
          notify();
          toast.error('Could not unsave profile');
          return;
        }
        toast(`Removed ${name || 'profile'} from Saved`);
      } else {
        savedIdsCache = savedIdsCache ?? new Set();
        savedIdsCache.add(targetId);
        notify();
        const { error } = await supabase
          .from('saved_profiles')
          .upsert(
            { user_id: user.id, saved_user_id: targetId },
            { onConflict: 'user_id,saved_user_id', ignoreDuplicates: true }
          );
        if (error) {
          savedIdsCache?.delete(targetId);
          notify();
          console.error('Save profile error:', error);
          toast.error('Could not save profile');
          return;
        }
        const onboarded = typeof window !== 'undefined' && window.localStorage.getItem(ONBOARDED_KEY) === '1';
        if (!onboarded) {
          ctxRef.current?.onFirstSave?.();
        } else {
          toast.success(`Saved ${name || 'profile'}`, {
            description: 'Find them in Community → Saved',
          });
        }
      }
    },
    [user]
  );

  const markOnboarded = useCallback(() => {
    try {
      window.localStorage.setItem(ONBOARDED_KEY, '1');
    } catch {
      // ignore
    }
  }, []);

  return { savedIds, isSaved, toggleSave, refresh, loading, markOnboarded };
};
